import React from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { Translate } from 'react-localize-redux';
import { type RouteComponentProps, Route, Switch } from 'react-router';

import LazyComponent from '@fd/ui/LazyComponent';

import { toKebabCase } from '../../helpers/strings';
import { lazyWithRetry } from '../../helpers/utilities';
import ErrorBoundary from '../../layouts/Portal/ErrorBoundary';
import { flagService } from '../../services/flagService/flagService';
import RestaurantLayoutRoutes from '../RestaurantLayout/RestaurantLayout.routes';
import { RMSModules } from '../RMS/utils/rmsModules';
import { RMSSubscriptions } from '../RMS/utils/rmsSubscriptions';
import DangerZoneList from './components/DangerZoneList';
import PropertyGeneralSettingsPage from './PropertyGeneralSettingsPage';
import PropertyManagement from './PropertyManagement';
import PropertyOpeningHoursPage from './PropertyOpeningHoursPage';

const RMSPage = lazyWithRetry(() => import('../RMS/components/RMSPage'));

type RouteParams = {
  appId?: string;
  propertyId?: string;
};

type Props = RouteComponentProps<RouteParams>;

const PropertySettingsRoutes: React.FC<React.PropsWithChildren<Props>> = (props: Props) => {
  const {
    match: { path, params },
  } = props;
  const toParentFromSettingsIframe = `/${params?.appId}/properties/${params?.propertyId}?tab=settings`;
  const basePropertyIframeUrl = 'configurations/franchisors/properties';
  const isShowCollectionSettingsAtPropertyLevelOn = useAppSelector((state) =>
    flagService.isFlagOn(state, 'showCollectionSettingsAtPropertyLevel')
  );
  return (
    <ErrorBoundary identifier="property-settings">
      <Switch>
        <Route exact path={`${path}/general-settings`} component={PropertyGeneralSettingsPage} />
        <Route
          exact
          path={`${path}/${toKebabCase(RMSModules.OPERATING_CONFIGURATION)}`}
          render={() => (
            <LazyComponent>
              <RMSPage
                toParent={toParentFromSettingsIframe}
                subscription={RMSSubscriptions.BASE}
                title={<Translate id="Operating_Configuration" />}
                module={RMSModules.OPERATING_CONFIGURATION}
                url={`${basePropertyIframeUrl}/${RMSModules.OPERATING_CONFIGURATION}`}
                propertyIdFromUrl
              />
            </LazyComponent>
          )}
        />
        <Route exact path={`${path}/layout`} component={RestaurantLayoutRoutes} />
        <Route
          exact
          path={`${path}/${toKebabCase(RMSModules.GEO_CONFIGURATION)}`}
          render={() => (
            <LazyComponent>
              <RMSPage
                subscription={RMSSubscriptions.BASE}
                title={<Translate id="Geo_Configuration" />}
                module={RMSModules.GEO_CONFIGURATION}
                url={`configurations/franchisors/properties/${RMSModules.GEO_CONFIGURATION}`}
                propertyIdFromUrl
                toParent={toParentFromSettingsIframe}
              />
            </LazyComponent>
          )}
        />
        <Route
          exact
          path={`${path}/${toKebabCase(RMSModules.DELIVERY_CONFIGURATION)}`}
          render={() => (
            <LazyComponent>
              <RMSPage
                subscription={RMSSubscriptions.BASE}
                title={<Translate id="Delivery_Management_Configuration" />}
                module={RMSModules.DELIVERY_CONFIGURATION}
                url={`configurations/franchisors/properties/${RMSModules.DELIVERY_CONFIGURATION}`}
                propertyIdFromUrl
                toParent={toParentFromSettingsIframe}
              />
            </LazyComponent>
          )}
        />
        {/* Address Mapping */}
        <Route
          exact
          path={`${path}/${toKebabCase(RMSModules.ADDRESS_MAPPING)}`}
          render={() => (
            <LazyComponent>
              <RMSPage
                subscription={RMSSubscriptions.BASE}
                title={<Translate id="Address_Mapping" />}
                module={RMSModules.ADDRESS_MAPPING}
                url={`configurations/system/${RMSModules.ADDRESS_MAPPING}`}
                toParent={toParentFromSettingsIframe}
                propertyIdFromUrl
              />
            </LazyComponent>
          )}
        />
        <Route
          exact
          path={`${path}/${toKebabCase(RMSModules.FLOAT_CONFIGURATION)}`}
          render={() => (
            <LazyComponent>
              <RMSPage
                subscription={RMSSubscriptions.BASE}
                title={<Translate id="Float_Configuration" />}
                module={RMSModules.FLOAT_CONFIGURATION}
                url={`configurations/franchisors/properties/${RMSModules.FLOAT_CONFIGURATION}`}
                propertyIdFromUrl
                toParent={toParentFromSettingsIframe}
              />
            </LazyComponent>
          )}
        />
        <Route
          exact
          path={`${path}/localisation-configuration`}
          render={() => (
            <LazyComponent>
              <RMSPage
                subscription={RMSSubscriptions.BASE}
                title={<Translate id="Localization_Configuration" />}
                module={RMSModules.LOCALIZATION_CONFIGURATION}
                url={`configurations/franchisors/properties/${RMSModules.LOCALIZATION_CONFIGURATION}`}
                propertyIdFromUrl
                toParent={toParentFromSettingsIframe}
              />
            </LazyComponent>
          )}
        />
        {isShowCollectionSettingsAtPropertyLevelOn && (
          <Route
            path={`${path}/settings`}
            render={(routeProps) => (
              <LazyComponent>
                <PropertyManagement
                  {...routeProps}
                  baseUrl={routeProps.match.url}
                  entityType="property"
                />
              </LazyComponent>
            )}
          />
        )}
        <Route exact path={`${path}/opening-hours`} component={PropertyOpeningHoursPage} />
        <Route exact path={`${path}/dangerzone`} component={DangerZoneList} />
      </Switch>
    </ErrorBoundary>
  );
};

export default PropertySettingsRoutes;
